import { useRef, useState } from 'react';
import { useAuth, useUser } from '../auth/AuthProvider';
import { documentsDueForReview, isOpen, ROLE_LABELS, sgarCount, sgarStatus } from '../data/domain';
import { useDocuments, useJobs, useRodentPlans } from '../data/hooks';
import { initials } from '../lib/format';
import { Link } from '../lib/router';
import { Icon, type IconName } from '../ui/Icon';
import { useDismiss } from '../ui/useDismiss';
import { Brand } from './Brand';
import { routePath, type ViewId } from './routes';

interface NavItem { view: ViewId; icon: IconName; label: string; managersOnly?: boolean; adminOnly?: boolean }
const primary: NavItem[] = [
  { view: 'dashboard', icon: 'home', label: 'Dashboard' },
  { view: 'clients', icon: 'clients', label: 'Clients' },
  { view: 'schedule', icon: 'calendar', label: 'Schedule' },
  { view: 'run-sheets', icon: 'list', label: 'Run sheets' },
  { view: 'map', icon: 'map', label: 'Service map' },
  { view: 'rodent', icon: 'target', label: 'Rodent planning' },
  { view: 'calculator', icon: 'flask', label: 'Chemical calculator' }
];
const operations: NavItem[] = [
  { view: 'technicians', icon: 'technician', label: 'Technicians', managersOnly: true },
  { view: 'documents', icon: 'file', label: 'Records' },
  { view: 'compliance', icon: 'shield', label: 'Compliance' },
  { view: 'users', icon: 'userCog', label: 'Users', adminOnly: true }
];

export function Sidebar({ activeNav, open, onClose, sidebarRef }: { activeNav: ViewId; open: boolean; onClose(): void; sidebarRef: React.RefObject<HTMLElement | null> }) {
  const user = useUser();
  const { signOut, openPassword } = useAuth();
  const jobs = useJobs();
  const documents = useDocuments();
  const plans = useRodentPlans();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useDismiss(menuOpen, [menuRef], () => setMenuOpen(false));

  const openJobs = jobs.filter(isOpen).length;
  const complianceAttention = documentsDueForReview(documents).length > 0 || plans.some((plan) => sgarCount(plan) && sgarStatus(plan).state !== 'current');
  const visible = (item: NavItem) => (!item.adminOnly || user.role === 'admin') && (!item.managersOnly || user.role !== 'tech');

  const link = (item: NavItem) => (
    <Link key={item.view} className={`nav-link${activeNav === item.view ? ' active' : ''}`} to={routePath(item.view)} onClick={onClose} aria-current={activeNav === item.view ? 'page' : undefined}>
      <Icon name={item.icon} />
      <span className="nav-text">{item.label}</span>
      {item.view === 'schedule' && openJobs > 0 && <span className="nav-badge" id="scheduleBadge" aria-label={`${openJobs} open jobs`}>{openJobs}</span>}
      {item.view === 'compliance' && complianceAttention && <span className="nav-alert" aria-label="Items need review" />}
    </Link>
  );

  return (
    <aside className={`sidebar${open ? ' open' : ''}`} ref={sidebarRef} aria-label="Workspace">
      <Link className="sidebar-brand" to="/" aria-label="PCS Internal home" onClick={onClose}><Brand /></Link>
      <nav className="sidebar-nav" aria-label="Primary navigation">
        {primary.filter(visible).map(link)}
        <p className="nav-label">Operations</p>
        {operations.filter(visible).map(link)}
      </nav>
      <div className="sidebar-footer" ref={menuRef}>
        <div className="org-card"><span className="org-mark" aria-hidden="true">PCS</span><span><b>PCS Pest Control</b><small>North Queensland</small></span></div>
        <button className="profile-button" id="accountMenuButton" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}>
          <span className="avatar" id="accountAvatar" aria-hidden="true">{initials(user.displayName)}</span>
          <span className="profile-text"><b id="accountDisplayName">{user.displayName}</b><small id="accountRoleLabel">{ROLE_LABELS[user.role] || 'Technician'}</small></span>
          <Icon name="more" />
        </button>
        {menuOpen && (
          <div className="menu account-menu" id="accountMenu" role="menu">
            {user.hasPassword && <button role="menuitem" onClick={() => { setMenuOpen(false); openPassword(); }}>Change password</button>}
            <button role="menuitem" onClick={() => { setMenuOpen(false); signOut(); }}>Sign out</button>
          </div>
        )}
      </div>
    </aside>
  );
}
